import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import AnalyticsQuerySettingsButton from 'insights/analytics-queries/settings/AnalyticsQuerySettingsButton';
import Breadcrumbs, {CrumbLink} from 'nav/Breadcrumbs';
import {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {ButtonSize} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import EditableBlockName from './EditableBlockName';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

interface Actions {
  deleteAnalyticsQuery: (analyticsQueryId: string) => void;
  openDashboardEditor: () => void;
  updateAnalyticsQuery: (analyticsQueryId: string, data: AnalyticsQuerySpecRequest) => Promise<void>;
}
export interface AnalyticsBlockEditorHeaderProps {
  actions: Actions;
  dashboardName: string;
  analyticsQuery: AnalyticsQuery;
}

function Divider(): JSX.Element {
  return <BenchlingIcon className="faded" icon="chevron" />;
}

export default function AnalyticsBlockEditorHeader(props: AnalyticsBlockEditorHeaderProps): JSX.Element {
  function handleDelete(): void {
    props.actions.deleteAnalyticsQuery(props.analyticsQuery.id);
  }

  const crumbs = [
    <CrumbLink
      key="analyticsDashboard"
      className="faded"
      dataTestAttrs={{component: 'AnalyticsBlockEditorHeader', element: 'dashboardLink'}}
      text={props.dashboardName}
      onClick={props.actions.openDashboardEditor}
    />,
    <EditableBlockName key="analyticsQuery" actions={props.actions} analyticsQuery={props.analyticsQuery} />,
  ];

  return (
    <span className="u-lr-split">
      <Breadcrumbs
        className="breadcrumbs--large u-vertical-center-items"
        crumbs={crumbs}
        divider={<Divider />}
      />
      <span>
        <AnalyticsQuerySettingsButton
          actions={props.actions}
          analyticsQuery={props.analyticsQuery}
          buttonSize={ButtonSize.SMALL}
          tooltipPlacement={TooltipPlacement.BOTTOM}
        />
        <OverflowMenu
          buttonProps={{
            size: ButtonSize.SMALL,
            dataTestAttrs: {component: 'AnalyticsBlockEditorHeader', element: 'overflowMenu'},
          }}
          enableTooltip={true}
          menuProps={{dropLeft: true}}
          options={[
            {
              icon: 'delete',
              contents: <span>Delete</span>,
              onSelect: handleDelete,
              dataTestAttrs: {
                component: 'AnalyticsBlockEditorHeader',
                element: 'menuOption',
                key: 'deleteBlock',
              },
            },
          ]}
          tooltipPlacement={FloatingPlacement.LEFT}
        />
      </span>
    </span>
  );
}
